import * as React from "react";
import {
    Link,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Tooltip,
    Tabs,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchBatchRemits } from "../../hooks/remits.queries";
import BackLink from "../../../../components/BackLink/BackLink";
import {
    addPracticeParamToUrl,
    cn,
    formatDate,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { RemitSource, RemitStatus } from "../../types/remits.types";
import MatchedRemitClaims from "../MatchedRemitClaims/MatchedRemitClaims";
import UnmatchedRemitClaims from "../UnmatchedRemitClaims/UnmatchedRemitClaims";
import {
    getBatchRemitAssigneeToDisplay,
    getRemitsListNavigateUrl,
} from "../../helpers/helpers.remits";
import {
    useAddEditBatchRemitAssignee,
    useUpdateRemitStatus,
} from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import EditManualRemitForm from "../EditManualRemitForm/EditManualRemitForm";
import { useGetClaim } from "../../../claims/hooks/claims.queries";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import InfoIcon from "../../../../icons/Info";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useAppSelector } from "../../../../hooks/useRedux";
import { PaymentStatus } from "../../../../utils/types/billing";

export default function RemitClaims() {
    const [searchParams] = useSearchParams();
    const [isEditRemitOpen, setIsEditRemitOpen] = React.useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const navigatedFrom = React.useRef(
        (location.state as { from?: string } | null)?.from
    );
    const isFromRemitBatch = navigatedFrom.current === "batch_remit";

    const params = useParams();
    const remitId = params.remitId || "";

    const noteId = searchParams.get("note_id");

    const { practice } = useAppSelector((state) => state.userPractice);

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const loggedInUser = useFetchUserDetails();

    const updateRemitStatus = useUpdateRemitStatus();
    const addEditBatchRemitAssignee = useAddEditBatchRemitAssignee();

    const remitsListUrl = getRemitsListNavigateUrl();

    const batchRemitData = useFetchBatchRemits(
        {
            batch_remit_id: remitId,
        },
        Boolean(remitId)
    );

    const { data: claimData } = useGetClaim(
        { note_id: noteId || "" },
        Boolean(noteId)
    );

    const claimInParamsDetails = claimData?.data[0];

    const batchRemitInView = batchRemitData?.data?.data[0];

    const batchAssignInfo = getBatchRemitAssigneeToDisplay(
        batchRemitInView?.batch_remit_assignees ?? null,
        loggedInUser?.data?.user_id ?? ""
    );

    const claimsFilterInLS = localStorage.getItem("claimslist_filters");
    const claimsTabInLS = claimsFilterInLS
        ? new URLSearchParams(claimsFilterInLS).get("tab")
        : null;

    const tabInUrl = searchParams.get("tab");

    const unmatchedPaymentsCount =
        batchRemitInView?.no_of_unmatched_payments ?? 0;

    const unmatchedPaymentsFilterParams = new URLSearchParams(searchParams);
    unmatchedPaymentsFilterParams.set("payment_status", PaymentStatus.PENDING);
    unmatchedPaymentsFilterParams.set("page", "1");
    unmatchedPaymentsFilterParams.set("tab", "claims");

    const isElectronicBatch =
        batchRemitInView?.remit_source === RemitSource.ELECTRONIC;
    const unmatchedRemitCount = new Set(
        (batchRemitInView?.unmatched_remits ?? []).map(
            (remit) => remit.claim_to_match ?? remit.remittance_id
        )
    ).size;

    const handleUpdateBatchRemitAssignee = (
        status: string,
        remittanceBatchIds?: string[]
    ) => {
        const dataToSend = {
            remittance_batch_ids: remittanceBatchIds || [remitId],
            status,
        };
        addEditBatchRemitAssignee.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries(["batch-remits"]);
                await queryClient.invalidateQueries([
                    "remit-claims",
                    handleRemoveEmptyParamFromQueryParams({
                        batch_remit_id: remitId,
                    }),
                ]);
                toast({
                    mode: "success",
                    message: res.message || "Batch remit assignment updated!",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action.",
                });
            },
        });
    };

    const handleChangeStatus = (remitStatus: string) => {
        const changeStatusPayload = {
            remittance_batch_ids: [remitId],
            status: remitStatus,
        };
        updateRemitStatus.mutate(changeStatusPayload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries([
                    "batch-remits",
                    { batch_remit_id: remitId },
                ]);
                toast({
                    mode: "success",
                    message:
                        res.message || "Remit status updated successfully.",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update remit status.",
                });
            },
        });
    };

    const getBatchRemitDataStates = () => {
        if (batchRemitData?.error) {
            return "0";
        }

        if (batchRemitData?.isLoading) {
            return <Loader loaderType="inline" />;
        }

        return "";
    };

    return (
        <>
            <Dialog open={isEditRemitOpen}>
                <DialogContent
                    variant="center"
                    handleCloseDialog={() => setIsEditRemitOpen(false)}
                    submitBtnFormValue="edit-remit-form"
                    saveText="Save changes"
                    title="Edit Manual Remit"
                >
                    <EditManualRemitForm
                        onFormSubmit={() => setIsEditRemitOpen(false)}
                    />
                </DialogContent>
            </Dialog>

            {!isFromRemitBatch && (
                <BackLink
                    onClick={() =>
                        claimInParamsDetails
                            ? navigate(
                                  `/claims/${claimInParamsDetails.claim_id}/${claimInParamsDetails.client_id}/${claimInParamsDetails.note_id}?key=claims&claims_view=view&viewFrom=claims&tab=${claimsTabInLS}&claims_sub_tab=claims&active_claim_id=${claimInParamsDetails.claim_id}`
                              )
                            : navigate(remitsListUrl)
                    }
                />
            )}

            <div className="mt-[--spacing-header]">
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex justify-between items-center mb-[--spacing-header] gap-[--spacing-items] flex-wrap">
                        <div className="flex items-center gap-x-2">
                            <h3 className="text-base font-extrabold">
                                Remit Summary
                            </h3>

                            {batchRemitInView && (
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>{`Status: ${
                                            getBatchRemitDataStates() ||
                                            makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    batchRemitInView?.remit_batch_status ||
                                                        ""
                                                )
                                            )
                                        }`}</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end">
                                        {batchRemitInView?.remit_batch_status !==
                                            RemitStatus.UN_POSTED && (
                                            <DropdownItem
                                                onClick={() => {
                                                    handleChangeStatus(
                                                        RemitStatus.UN_POSTED
                                                    );
                                                }}
                                            >
                                                Mark as Unposted
                                            </DropdownItem>
                                        )}
                                        {batchRemitInView?.remit_batch_status !==
                                            RemitStatus.POSTED && (
                                            <DropdownItem
                                                onClick={() => {
                                                    handleChangeStatus(
                                                        RemitStatus.POSTED
                                                    );
                                                }}
                                            >
                                                Mark as Posted
                                            </DropdownItem>
                                        )}
                                        {batchRemitInView?.remit_batch_status !==
                                            RemitStatus.ON_HOLD && (
                                            <DropdownItem
                                                onClick={() => {
                                                    handleChangeStatus(
                                                        RemitStatus.ON_HOLD
                                                    );
                                                }}
                                            >
                                                Mark as On Hold
                                            </DropdownItem>
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            )}
                        </div>
                        <div className="flex gap-x-2 items-center">
                            {batchRemitInView &&
                                batchRemitInView.remit_source ===
                                    RemitSource.MANUAL && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => setIsEditRemitOpen(true)}
                                    >
                                        Edit Remit
                                    </Button>
                                )}

                            {batchRemitInView?.remit_batch_eob_url && (
                                <Link
                                    to={addPracticeParamToUrl(
                                        `/remits/${remitId}/claims/EOB`,
                                        practice?.practice_id
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="bg-primary text-white text-sm text-base font-medium px-3 h-7 rounded-md flex items-center justify-center border-none focus-visible:[outline:2px_solid_var(--color-interactive-focus)]"
                                >
                                    View EOB
                                </Link>
                            )}
                            {batchRemitInView && (
                                <Button
                                    onClick={() =>
                                        handleUpdateBatchRemitAssignee(
                                            batchAssignInfo.isAssignedToMe
                                                ? "unlocked"
                                                : "in_progress"
                                        )
                                    }
                                    disabled={
                                        addEditBatchRemitAssignee.isLoading
                                    }
                                >
                                    {batchAssignInfo.isAssignedToMe
                                        ? "Unassign batch"
                                        : "Assign batch to me"}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div
                        className={cn(
                            "grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))]   gap-[--spacing-items]"
                        )}
                    >
                        <div>
                            <p className="section-info">Check date</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    (batchRemitInView?.remit_received_date
                                        ? formatDate(
                                              batchRemitInView.remit_received_date.replace(
                                                  /(\+00:00|Z)$/,
                                                  ""
                                              ),
                                              true
                                          )
                                        : "--")}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Insurance</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchRemitInView?.insurance_provider_name ||
                                    "--"}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Check number</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchRemitInView?.remit_batch_ref_id ||
                                    "--"}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Remit Payment Ref</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchRemitInView?.remit_batch_payment_ref ||
                                    "--"}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Check amount</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    showMoneyInAppFormat(
                                        batchRemitInView?.remit_amount || 0
                                    ) ||
                                    0}
                            </p>
                        </div>
                        <div>
                            <p className="section-info flex items-center gap-1">
                                Claims with payment
                                <Tooltip content="Number of claims submitted on Mantle paid in this remit">
                                    <Button variant="normal" size="auto">
                                        <InfoIcon />
                                    </Button>
                                </Tooltip>
                            </p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchRemitInView?.no_of_claims_payments ||
                                    0}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Matched payments</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchRemitInView?.no_of_matched_payments ||
                                    0}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Matched amount</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    showMoneyInAppFormat(
                                        batchRemitInView?.amount_of_matched_payments ||
                                            0
                                    )}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Unmatched payments</p>
                            {unmatchedPaymentsCount > 0 ? (
                                <Link
                                    to={`${
                                        location.pathname
                                    }?${unmatchedPaymentsFilterParams.toString()}`}
                                    className="text-textLink section-value underline"
                                >
                                    {unmatchedPaymentsCount}
                                </Link>
                            ) : (
                                <p className="section-value">
                                    {getBatchRemitDataStates() || 0}
                                </p>
                            )}
                        </div>
                        <div>
                            <p className="section-info">Unmatched amount</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    showMoneyInAppFormat(
                                        batchRemitInView?.amount_of_unmatched_payments ||
                                            0
                                    )}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">Batch assigned to</p>
                            <p className="section-value">
                                {getBatchRemitDataStates() ||
                                    batchAssignInfo.usersNameToDisplay}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            <Card>
                <Tabs
                    className="w-full"
                    defaultActiveKey={tabInUrl || "claims"}
                    key={tabInUrl}
                    items={[
                        {
                            label: "Matched claims",
                            key: "claims",
                            children: (
                                <MatchedRemitClaims
                                    batchRemitInView={batchRemitInView}
                                />
                            ),
                        },
                        {
                            label: "Unmatched",
                            key: "unmatched",
                            count: String(unmatchedRemitCount),
                            hide: !(
                                isElectronicBatch && unmatchedRemitCount > 0
                            ),
                            children: (
                                <UnmatchedRemitClaims
                                    unmatchedRemits={
                                        batchRemitInView?.unmatched_remits ?? []
                                    }
                                    batchInsuranceProviderId={
                                        batchRemitInView?.insurance_provider_id
                                    }
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </>
    );
}
